import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useSubscriptionPlans, useUpgradeSubscriptionForUser } from '../../../subscription/hooks/use-subscription';
import { useToast } from '../../../../hooks/use-toast';
import { PLANS } from 'constant/plan';

interface ChangePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  currentPlan?: {
    itemId: string;
    name: string;
  };
  currentPlanItem?: {
    itemId: string;
    title: string | null;
  };
}

type BillingFrequency = 'monthly' | 'yearly';

export function ChangePlanDialog({ open, onOpenChange, userId, userName, currentPlan, currentPlanItem }: ChangePlanDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>('monthly');

  // Use the subscription plans hook
  const { data: plansResponse, isLoading: isLoadingPlans, error: plansError } = useSubscriptionPlans();

  // Use the upgrade subscription hook
  const { mutate: upgradeSubscription, isPending: isUpgrading } = useUpgradeSubscriptionForUser({
    onSuccess: () => {
      // Invalidate the users query to refresh the IAM table data
      queryClient.invalidateQueries({ queryKey: ['getUsers'] });
      onOpenChange(false);
    },
  });

  // Extract plans from response and memoize to prevent unnecessary re-renders
  const plans = React.useMemo(() => plansResponse?.data || [], [plansResponse?.data]);

  // Set current plan or first plan as default when plans are loaded
  useEffect(() => {
    if (plans.length > 0 && open) {
      // If user has a current plan and it's not null/NoPlan, find matching plan
      if (currentPlan && currentPlan.itemId && currentPlan.name && currentPlan.name !== PLANS.NOPLAN) {
        // First try to match by itemId
        let matchingPlan = plans.find(plan => plan.itemId === currentPlan.itemId);

        if (!matchingPlan) {
          // Fallback: try to match by plan name/title (case insensitive)
          matchingPlan = plans.find(plan =>
            plan.title?.toLowerCase().includes(currentPlan.name.toLowerCase())
          );
        }

        if (matchingPlan) {
          setSelectedPlan(matchingPlan.itemId);
        } else {
          // If no match found, default to first plan
          setSelectedPlan(plans[0]?.itemId || '');
        }
      } else {
        // No current plan or it's NoPlan, default to first plan
        setSelectedPlan(plans[0]?.itemId || '');
      }
    }
  }, [plans, currentPlan, open]);

  const selectedPlanData = plans.find(plan => plan.itemId === selectedPlan);

  // Check if user has an existing plan item
  const hasExistingPlanItem = !!(currentPlanItem && currentPlanItem.itemId) && !(currentPlan?.name === PLANS.FREE || currentPlan?.name === PLANS.NOPLAN);

  // Set billing frequency when dialog opens based on planItem.itemId
  useEffect(() => {
    console.log('currentPlanItem', currentPlanItem);
    if (open && currentPlanItem && currentPlanItem.itemId && selectedPlanData) {
      // Find the matching plan item in the selected plan's items array
      const matchingPlanItem = selectedPlanData.items?.find(
        item => item.itemId === currentPlanItem.itemId
      );

      if (matchingPlanItem) {
        // Determine billing frequency from the plan item
        const title = matchingPlanItem.title?.toLowerCase() || '';
        if (title.includes('annually') || title.includes('yearly')) {
          setBillingFrequency('yearly');
        } else if (title.includes('monthly')) {
          setBillingFrequency('monthly');
        } else {
          setBillingFrequency('monthly'); // default
        }
      } else {
        setBillingFrequency('monthly'); // default if no match
      }
    } else if (open) {
      // Reset to default if no current plan item
      setBillingFrequency('monthly');
    }
  }, [open, currentPlanItem, selectedPlanData]);

  const getSelectedPlanItemPrice = () => {
    if (!selectedPlanData || !selectedPlanData.items) return 0;

    // Find the plan item that matches the selected billing frequency
    const selectedPlanItem = selectedPlanData.items.find(item => {
      const title = item.title?.toLowerCase() || '';
      if (billingFrequency === 'yearly') {
        return title.includes('annually') || title.includes('yearly');
      } else {
        return title.includes('monthly');
      }
    });

    return selectedPlanItem ? selectedPlanItem.totalPrice : selectedPlanData.items[0]?.totalPrice || 0;
  };

  const isMonthlyAvailable = React.useCallback(() => {
    if (!selectedPlanData || !selectedPlanData.items) return false;
    return selectedPlanData.items.some(item => {
      const title = item.title?.toLowerCase() || '';
      return title.includes('monthly') || title.includes('month');
    });
  }, [selectedPlanData]);

  const isYearlyAvailable = React.useCallback(() => {
    if (!selectedPlanData || !selectedPlanData.items) return false;
    return selectedPlanData.items.some(item => {
      const title = item.title?.toLowerCase() || '';
      return title.includes('annually') || title.includes('yearly');
    });
  }, [selectedPlanData]);

  const getSelectedPlanItemId = () => {
    if (!selectedPlanData || !selectedPlanData.items) return '';

    // Find the plan item that matches the selected billing frequency
    const selectedPlanItem = selectedPlanData.items.find(item => {
      const title = item.title?.toLowerCase() || '';
      if (billingFrequency === 'yearly') {
        return title.includes('annually') || title.includes('yearly');
      } else {
        return title.includes('monthly');
      }
    });

    return selectedPlanItem ? selectedPlanItem.itemId : selectedPlanData.items[0]?.itemId || '';
  };

  // Auto-switch billing frequency if current selection is not available
  React.useEffect(() => {
    if (selectedPlanData) {
      const monthlyAvailable = isMonthlyAvailable();
      const yearlyAvailable = isYearlyAvailable();

      // If current selection is not available, switch to available option
      if (billingFrequency === 'monthly' && !monthlyAvailable && yearlyAvailable) {
        setBillingFrequency('yearly');
      } else if (billingFrequency === 'yearly' && !yearlyAvailable && monthlyAvailable) {
        setBillingFrequency('monthly');
      }
    }
  }, [selectedPlanData, billingFrequency, isMonthlyAvailable, isYearlyAvailable]);

  const handleSave = () => {
    if (!selectedPlan) {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('PLEASE_SELECT_A_PLAN') || 'Please select a plan',
      });
      return;
    }

    const planItemId = getSelectedPlanItemId();
    if (!planItemId) {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('INVALID_PLAN_ITEM') || 'Invalid plan item selected',
      });
      return;
    }

    upgradeSubscription({
      userId: userId,
      planId: selectedPlan,
      planItemId: planItemId,
    });
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('CHANGE_PLAN_FOR_USER') || 'Change Plan for User'}</DialogTitle>
        </DialogHeader>

        {isLoadingPlans ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4 h-[448px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : plansError ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-4 h-[448px]">
            <p className="text-sm text-destructive">{t('FAILED_TO_LOAD_PLANS') || 'Failed to load subscription plans'}</p>
            <Button variant="outline" onClick={() => window.location.reload()}>
              {t('RETRY') || 'Retry'}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* User Info */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                {t('CHANGING_PLAN_FOR') || 'Changing plan for'}: <span className="font-medium text-foreground">{userName}</span>
              </p>
              {currentPlan && currentPlan.name && currentPlan.name !== PLANS.NOPLAN && (
                <p className="text-sm text-muted-foreground mt-1">
                  {t('CURRENT_PLAN') || 'Current plan'}: <span className="font-medium text-foreground">{t(currentPlan.name)}</span>
                  {currentPlanItem && currentPlanItem.title && (
                    <span className="ml-1">({currentPlanItem.title})</span>
                  )}
                </p>
              )}
            </div>

            {/* Plan Selection */}
            <div className="space-y-2">
              <Label htmlFor="plan-select">{t('SELECT_PLAN') || 'Select Plan'}</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger id="plan-select">
                  <SelectValue placeholder={t('SELECT_A_PLAN') || 'Select a plan'} />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan) => (
                    <SelectItem key={plan.itemId} value={plan.itemId}>
                      <div className="flex flex-col">
                        <span className="font-medium">{plan.title}</span>
                        {/* <span className="text-sm text-muted-foreground">{plan.description}</span> */}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Billing Frequency */}
            <div className="space-y-3">
              <Label>{t('BILLING_FREQUENCY') || 'Billing Frequency'}</Label>
              {hasExistingPlanItem && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    {t('BILLING_PERIOD_RESTRICTION') || 'Plan changes must maintain the same billing period (monthly to monthly or yearly to yearly)'}
                  </p>
                </div>
              )}
              <RadioGroup value={billingFrequency} onValueChange={(value: BillingFrequency) => setBillingFrequency(value)}>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="monthly"
                      id="monthly"
                      disabled={!isMonthlyAvailable() || hasExistingPlanItem}
                    />
                    <Label
                      htmlFor="monthly"
                      className={(!isMonthlyAvailable() || hasExistingPlanItem) ? 'text-muted-foreground cursor-not-allowed' : 'cursor-pointer'}
                    >
                      {t('MONTHLY') || 'Monthly'}
                      {!isMonthlyAvailable() && (
                        <span className="text-xs text-muted-foreground ml-1">(Not Available)</span>
                      )}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="yearly"
                      id="yearly"
                      disabled={!isYearlyAvailable() || hasExistingPlanItem}
                    />
                    <Label
                      htmlFor="yearly"
                      className={(!isYearlyAvailable() || hasExistingPlanItem) ? 'text-muted-foreground cursor-not-allowed' : 'cursor-pointer'}
                    >
                      {t('YEARLY') || 'Yearly'}
                      {!isYearlyAvailable() && (
                        <span className="text-xs text-muted-foreground ml-1">(Not Available)</span>
                      )}
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Total Amount */}
            {selectedPlanData && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{t('TOTAL') || 'Total Amount'}:</span>
                  <span className="text-lg font-bold">
                    {selectedPlanData.currency} {getSelectedPlanItemPrice().toFixed(2)}
                  </span>
                </div>
                {billingFrequency === 'yearly' && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('BILLED_ANNUALLY') || 'Billed annually'}
                  </p>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={handleCancel} disabled={isUpgrading}>
                {t('CANCEL') || 'Cancel'}
              </Button>
              <Button onClick={handleSave} disabled={isUpgrading || !selectedPlan}>
                {isUpgrading ? t('SAVING') || 'Saving...' : t('SAVE') || 'Save'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

